import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { InvitePanel } from '../components/InvitePanel';
import { useAuth } from '../contexts/AuthContext';
import {
  ApiError,
  activitiesApi,
  classesApi,
  exportAiApi,
  groupsApi,
  usersApi,
  type Activity,
  type ActivityFlowType,
  type ActivityType,
  type ClassDeletionPreview,
  type ClassMember,
  type Group,
  type SchoolClass,
} from '../services/api/client';

const LEVEL_LABEL = {
  UNDERGRADUATE: 'Graduação',
  GRADUATE: 'Pós-graduação',
} as const;

export default function ClassDetailPage() {
  const { classId = '' } = useParams();
  const { user } = useAuth();
  const isProfessor = user?.role === 'PROFESSOR';

  const [schoolClass, setSchoolClass] = useState<SchoolClass | null>(null);
  const [error, setError] = useState('');
  const [deleted, setDeleted] = useState(false);

  const reloadClass = useCallback(() => {
    classesApi
      .get(classId)
      .then((result) => setSchoolClass(result.class))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar a turma.'),
      );
  }, [classId]);

  useEffect(() => {
    reloadClass();
  }, [reloadClass]);

  // A turma foi excluída (PRD 34.7) — nada mais a mostrar aqui, volta para a
  // listagem.
  if (deleted) return <Navigate to="/turmas" replace />;

  if (error) {
    return (
      <>
        <p className="form-error">{error}</p>
        <Link to="/turmas">Voltar para turmas</Link>
      </>
    );
  }

  if (!schoolClass) return <p className="muted">Carregando…</p>;

  return (
    <>
      <div className="section-head">
        <div>
          <h2>
            {schoolClass.name}
            {schoolClass.status === 'ARCHIVED' && (
              <span className="badge" style={{ marginLeft: '0.6rem' }}>
                Arquivada
              </span>
            )}
          </h2>
          <p className="muted">
            {schoolClass.discipline} · {schoolClass.course} ·{' '}
            {LEVEL_LABEL[schoolClass.level]} · {schoolClass.academicTerm}
          </p>
        </div>
        <div className="button-row">
          {isProfessor && (
            <Link to={`/turmas/${classId}/dashboard`} className="button-link">
              Dashboard
            </Link>
          )}
          <Link to="/turmas" className="button-link">
            Voltar
          </Link>
        </div>
      </div>

      {isProfessor ? (
        <ProfessorView
          classId={classId}
          schoolClass={schoolClass}
          onClassChange={reloadClass}
          onClassDeleted={() => setDeleted(true)}
        />
      ) : (
        <StudentView classId={classId} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Professor
// ---------------------------------------------------------------------------

function ProfessorView({
  classId,
  schoolClass,
  onClassChange,
  onClassDeleted,
}: {
  classId: string;
  schoolClass: SchoolClass;
  onClassChange: () => void;
  onClassDeleted: () => void;
}) {
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const [membersResult, groupsResult] = await Promise.all([
        classesApi.members(classId),
        groupsApi.list(classId),
      ]);

      setMembers(membersResult.members);
      setGroups(groupsResult.groups);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar.');
    }
  }, [classId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      {error && <p className="form-error">{error}</p>}

      <ClassSettingsSection classId={classId} schoolClass={schoolClass} onChange={onClassChange} />

      <InvitePanel
        title="QR Code de matrícula"
        description="Projete em sala. Quem escanear entra nesta turma — mas não em nenhum grupo."
        load={() => classesApi.invite(classId)}
        rotate={() => classesApi.rotateInvite(classId)}
        cancel={() => classesApi.cancelInvite(classId)}
      />

      <ActivitiesSection classId={classId} canCreate />
      <MembersSection
        classId={classId}
        members={members}
        onChange={() => void reload()}
      />
      <GroupsSection
        classId={classId}
        groups={groups}
        members={members}
        onChange={() => void reload()}
      />

      {schoolClass.status === 'ARCHIVED' && (
        <DangerZoneSection classId={classId} schoolClass={schoolClass} onDeleted={onClassDeleted} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Situação da turma: arquivar/reativar e Meus Fluxos (PRD 34.5, 34.6)
// ---------------------------------------------------------------------------

function ClassSettingsSection({
  classId,
  schoolClass,
  onChange,
}: {
  classId: string;
  schoolClass: SchoolClass;
  onChange: () => void;
}) {
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const isArchived = schoolClass.status === 'ARCHIVED';

  async function handleToggleArchive() {
    setError('');

    if (!isArchived) {
      const confirmed = window.confirm(
        'Arquivar esta turma?\n\n' +
          'Os alunos perdem o acesso a tudo — inclusive leitura — e não conseguem mais ' +
          'exportar o próprio trabalho. Se algum aluno ainda precisa baixar o trabalho dele, ' +
          'peça que exporte ANTES de arquivar.\n\n' +
          'É reversível: você pode reativar a turma depois.',
      );
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      await classesApi.update(classId, { status: isArchived ? 'ACTIVE' : 'ARCHIVED' });
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao atualizar a turma.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleMyFlows(checked: boolean) {
    setError('');
    setSaving(true);
    try {
      await classesApi.update(classId, { myFlowsEnabled: checked });
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao atualizar a turma.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h3>Situação da turma</h3>
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="checkbox-field">
        <input
          id="my-flows-enabled"
          type="checkbox"
          checked={schoolClass.myFlowsEnabled}
          disabled={saving || isArchived}
          onChange={(e) => void handleToggleMyFlows(e.target.checked)}
        />
        <label htmlFor="my-flows-enabled">Habilitar Meus Fluxos para esta turma</label>
      </div>
      <p className="field-hint">
        Libera a área de desenho livre (fora das atividades) para quem está matriculado aqui.
        Desligar esconde a área — os fluxos que os alunos já criaram não são apagados.
      </p>

      <div className="button-row" style={{ marginTop: '0.75rem' }}>
        <button type="button" onClick={() => void handleToggleArchive()} disabled={saving}>
          {isArchived ? 'Reativar turma' : 'Arquivar turma'}
        </button>
      </div>
      {isArchived && (
        <p className="field-hint">
          Turma arquivada: alunos não têm mais acesso a nada dela. Reative para devolver o acesso.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Exclusão da turma (PRD 34.7) — só aparece com a turma já arquivada.
// ---------------------------------------------------------------------------

function DangerZoneSection({
  classId,
  schoolClass,
  onDeleted,
}: {
  classId: string;
  schoolClass: SchoolClass;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<ClassDeletionPreview | null>(null);
  const [deleteAccounts, setDeleteAccounts] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  async function handleOpen() {
    setOpen(true);
    setError('');
    try {
      setPreview(await classesApi.deletePreview(classId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar a prévia.');
    }
  }

  async function handleDelete(event: FormEvent) {
    event.preventDefault();
    setError('');
    setDeleting(true);

    try {
      await classesApi.remove(classId, { confirmName, deleteAccounts });
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao excluir a turma.');
      setDeleting(false);
    }
  }

  return (
    <section className="card danger-zone">
      <div className="card-head">
        <h3>Excluir turma</h3>
      </div>

      <p className="muted small">
        Definitivo — apaga a turma, seus grupos, atividades e todos os projetos (individuais e em
        grupo), com todo o conteúdo de todos os módulos.
      </p>

      {!open && (
        <button type="button" className="link-danger" onClick={() => void handleOpen()}>
          Excluir turma "{schoolClass.name}"
        </button>
      )}

      {open && error && <p className="form-error">{error}</p>}

      {open && !preview && !error && <p className="muted">Carregando prévia…</p>}

      {open && preview && (
        <form onSubmit={handleDelete} className="inline-form" noValidate>
          <ul className="simple-list">
            <li>{preview.memberCount} aluno(s) matriculado(s)</li>
            <li>{preview.activityCount} atividade(s)</li>
            <li>{preview.instanceCount} projeto(s) (individuais e em grupo)</li>
          </ul>

          <div className="checkbox-field">
            <input
              id="delete-accounts"
              type="checkbox"
              checked={deleteAccounts}
              onChange={(e) => setDeleteAccounts(e.target.checked)}
            />
            <label htmlFor="delete-accounts">
              Apagar também as contas dos alunos ({preview.deletableAccountCount} seriam apagadas
              {preview.sparedAccountCount > 0 &&
                `, ${preview.sparedAccountCount} poupada(s) por estar(em) em outra turma`}
              )
            </label>
          </div>
          <p className="field-hint">
            Quem está matriculado em outra turma sua nunca é apagado por aqui — só perde esta
            turma. Meus Fluxos e o histórico da pessoa em outros lugares vão junto com a conta.
          </p>

          <label htmlFor="confirm-class-name">
            Digite o nome da turma (<strong>{schoolClass.name}</strong>) para confirmar
          </label>
          <input
            id="confirm-class-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            required
          />

          <div className="button-row" style={{ marginTop: '0.75rem' }}>
            <button
              type="submit"
              className="link-danger"
              disabled={deleting || confirmName.trim() !== schoolClass.name}
            >
              {deleting ? 'Excluindo…' : 'Excluir definitivamente'}
            </button>
            <button type="button" onClick={() => setOpen(false)} disabled={deleting}>
              Cancelar
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Atividades (professor e aluno)
// ---------------------------------------------------------------------------

const ACTIVITY_STATUS_LABEL = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicada',
  ARCHIVED: 'Encerrada',
} as const;

function ActivitiesSection({
  classId,
  canCreate = false,
}: {
  classId: string;
  canCreate?: boolean;
}) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<ActivityType>('GROUP');
  const [flowType, setFlowType] = useState<ActivityFlowType>('AS_IS');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await activitiesApi.list(classId);
      setActivities(result.activities);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar atividades.');
    }
  }, [classId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await activitiesApi.create(classId, {
        title,
        ...(description ? { description } : {}),
        type,
        ...(type === 'GROUP' ? { flowType } : {}),
      });

      setTitle('');
      setDescription('');
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao criar a atividade.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h3>Atividades ({activities.length})</h3>
        {canCreate && (
          <button type="button" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Cancelar' : 'Nova atividade'}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="inline-form" noValidate>
          <label htmlFor="activity-title">Título</label>
          <input
            id="activity-title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Mapeamento do processo de atendimento"
          />

          <label htmlFor="activity-desc">Enunciado</label>
          <textarea
            id="activity-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Opcional — o que os alunos devem fazer"
          />

          <div className="form-row">
            <div>
              <label htmlFor="activity-type">Tipo</label>
              <select
                id="activity-type"
                value={type}
                onChange={(e) => setType(e.target.value as ActivityType)}
              >
                <option value="GROUP">Em grupo</option>
                <option value="INDIVIDUAL">Individual</option>
              </select>
            </div>

            {type === 'GROUP' && (
              <div>
                <label htmlFor="activity-flow">Classificação</label>
                <select
                  id="activity-flow"
                  value={flowType}
                  onChange={(e) => setFlowType(e.target.value as ActivityFlowType)}
                >
                  <option value="AS_IS">AS-IS (situação atual)</option>
                  <option value="TO_BE">TO-BE (situação proposta)</option>
                </select>
              </div>
            )}
          </div>

          <p className="field-hint">
            Atividades individuais não têm Análise do Processo nem Ishikawa.
          </p>

          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Criando…' : 'Criar atividade'}
          </button>
        </form>
      )}

      {error && <p className="form-error">{error}</p>}

      {activities.length === 0 ? (
        <p className="muted">
          {canCreate
            ? 'Nenhuma atividade ainda.'
            : 'Nenhuma atividade disponível. O professor publica as atividades conforme a disciplina avança.'}
        </p>
      ) : (
        <ul className="simple-list">
          {activities.map((activity) => (
            <li key={activity.id}>
              <Link to={`/turmas/${classId}/atividades/${activity.id}`}>
                {activity.title}
              </Link>
              <span className="muted small">
                {activity.type === 'GROUP' ? 'Em grupo' : 'Individual'}
                {activity.flowType &&
                  ` · ${activity.flowType === 'AS_IS' ? 'AS-IS' : 'TO-BE'}`}
                {canCreate && ` · ${ACTIVITY_STATUS_LABEL[activity.status]}`}
                {activity.releasedModules.length > 0 &&
                  ` · ${activity.releasedModules.length} módulo(s) liberado(s)`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MembersSection({
  classId,
  members,
  onChange,
}: {
  classId: string;
  members: ClassMember[];
  onChange: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [registration, setRegistration] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await classesApi.addMember(classId, {
        name,
        email,
        ...(registration ? { registrationNumber: registration } : {}),
      });

      setName('');
      setEmail('');
      setRegistration('');
      setShowForm(false);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao cadastrar.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(member: ClassMember) {
    const confirmed = window.confirm(
      `Remover ${member.name} da turma? Ele sai também dos grupos desta turma.`,
    );

    if (!confirmed) return;

    try {
      await classesApi.removeMember(classId, member.id);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao remover.');
    }
  }

  /**
   * Exclusão definitiva da conta (PRD 34.4) — diferente de "Remover" (que só
   * tira da turma). Confirmação por nome digitado: um clique de mais aqui
   * apaga a pessoa da plataforma inteira, não só desta turma.
   */
  async function handleDeleteAccount(member: ClassMember) {
    const typed = window.prompt(
      `Isso EXCLUI A CONTA de ${member.name} da plataforma inteira — não só desta turma. ` +
        'O trabalho em grupo dele sobrevive (sem a autoria), mas o trabalho individual e o ' +
        'Meus Fluxos são apagados. Não há como desfazer.\n\n' +
        `Digite o nome "${member.name}" para confirmar:`,
    );

    if (typed === null) return;
    if (typed.trim() !== member.name) {
      window.alert('Nome digitado não confere — nada foi excluído.');
      return;
    }

    try {
      await usersApi.deleteStudent(member.userId);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao excluir a conta.');
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h3>Alunos ({members.length})</h3>
        <button type="button" onClick={() => setShowForm((value) => !value)}>
          {showForm ? 'Cancelar' : 'Cadastrar aluno'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="inline-form" noValidate>
          <p className="muted">
            O aluno recebe um link de ativação individual (por e-mail, se configurado, e sempre
            disponível para copiar na lista abaixo) — é por esse link que ele define a senha.
          </p>

          <div className="form-row">
            <div>
              <label htmlFor="member-name">Nome</label>
              <input
                id="member-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="member-email">E-mail</label>
              <input
                id="member-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="member-reg">Matrícula</label>
              <input
                id="member-reg"
                value={registration}
                onChange={(e) => setRegistration(e.target.value)}
                placeholder="opcional"
              />
            </div>
          </div>

          <button type="submit" className="primary" disabled={saving}>
            {saving ? 'Cadastrando…' : 'Cadastrar'}
          </button>
        </form>
      )}

      {error && <p className="form-error">{error}</p>}

      {members.length === 0 ? (
        <p className="muted">
          Nenhum aluno ainda. Projete o QR Code acima ou cadastre manualmente.
        </p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Matrícula</th>
              <th>Grupos</th>
              <th>Situação</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <td>
                  {member.name}
                  <br />
                  <span className="muted small">{member.email}</span>
                </td>
                <td className="muted">{member.registrationNumber ?? '—'}</td>
                <td className="muted">
                  {member.groups.length === 0
                    ? '—'
                    : member.groups.map((group) => group.name).join(', ')}
                </td>
                <td>
                  {member.activated ? (
                    <span className="badge badge-ok">Ativo</span>
                  ) : (
                    <span className="badge badge-pending">Não ativado</span>
                  )}
                </td>
                <td>
                  <div className="button-row">
                    {!member.activated && <ActivationLinkButton classId={classId} member={member} />}
                    {member.activated && <ResetPasswordButton classId={classId} member={member} />}
                    <button
                      type="button"
                      className="link-danger"
                      onClick={() => void handleRemove(member)}
                    >
                      Remover
                    </button>
                    <button
                      type="button"
                      className="link-danger"
                      onClick={() => void handleDeleteAccount(member)}
                    >
                      Excluir conta
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Link de ativação individual de um aluno pré-cadastrado (PRD 33.7). Busca
 * sob demanda (não a cada render da lista inteira) e some sozinho depois de
 * copiar — o professor não precisa de um painel permanente por aluno.
 */
function ActivationLinkButton({ classId, member }: { classId: string; member: ClassMember }) {
  const [state, setState] = useState<
    { url: string; expiresAt: string; copied: boolean } | 'loading' | null
  >(null);
  const [error, setError] = useState('');

  async function open() {
    setState('loading');
    setError('');

    try {
      const link = await classesApi.activationLink(classId, member.id);

      if (!link.url) {
        setError('Esta conta já foi ativada.');
        setState(null);
        return;
      }

      setState({ url: link.url, expiresAt: link.expiresAt!, copied: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao carregar o link.');
      setState(null);
    }
  }

  async function handleCopy() {
    if (state === 'loading' || !state) return;

    await navigator.clipboard.writeText(state.url);
    setState({ ...state, copied: true });
  }

  async function handleRegenerate() {
    setState('loading');
    setError('');

    try {
      const link = await classesApi.regenerateActivationLink(classId, member.id);
      setState({ url: link.url, expiresAt: link.expiresAt, copied: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao gerar novo link.');
      setState(null);
    }
  }

  if (state === null) {
    return (
      <>
        <button type="button" onClick={() => void open()}>
          Link de ativação
        </button>
        {error && <span className="form-error small">{error}</span>}
      </>
    );
  }

  if (state === 'loading') {
    return <span className="muted small">Carregando…</span>;
  }

  const expired = new Date(state.expiresAt).getTime() <= Date.now();

  return (
    <span className="activation-link-inline">
      <button type="button" onClick={() => void handleCopy()} disabled={expired}>
        {state.copied ? 'Copiado' : 'Copiar link'}
      </button>
      <button type="button" onClick={() => void handleRegenerate()}>
        Gerar novo
      </button>
      <span className={expired ? 'form-error small' : 'muted small'}>
        {expired
          ? 'Expirado'
          : `Válido até ${new Date(state.expiresAt).toLocaleDateString('pt-BR')}`}
      </span>
    </span>
  );
}

/**
 * Redefinição de senha de aluno já ativado (PRD 34.3) — gera um link novo
 * direto (sem "peek", ao contrário da ativação: aqui sempre é um pedido
 * novo, não a exibição de algo pendente). A senha antiga continua válida até
 * o aluno abrir o link — não é o professor quem escolhe a senha nova.
 */
function ResetPasswordButton({ classId, member }: { classId: string; member: ClassMember }) {
  const [state, setState] = useState<
    { url: string; expiresAt: string; copied: boolean } | 'loading' | null
  >(null);
  const [error, setError] = useState('');

  async function handleGenerate() {
    setState('loading');
    setError('');

    try {
      const link = await classesApi.resetMemberPassword(classId, member.id);
      setState({ url: link.url, expiresAt: link.expiresAt, copied: false });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao gerar o link.');
      setState(null);
    }
  }

  async function handleCopy() {
    if (state === 'loading' || !state) return;

    await navigator.clipboard.writeText(state.url);
    setState({ ...state, copied: true });
  }

  if (state === null) {
    return (
      <>
        <button type="button" onClick={() => void handleGenerate()}>
          Redefinir senha
        </button>
        {error && <span className="form-error small">{error}</span>}
      </>
    );
  }

  if (state === 'loading') {
    return <span className="muted small">Gerando…</span>;
  }

  return (
    <span className="activation-link-inline">
      <button type="button" onClick={() => void handleCopy()}>
        {state.copied ? 'Copiado' : 'Copiar link'}
      </button>
      <span className="muted small">
        Entregue ao aluno — ele define a senha nova. Válido até{' '}
        {new Date(state.expiresAt).toLocaleDateString('pt-BR')}.
      </span>
    </span>
  );
}

function GroupsSection({
  classId,
  groups,
  members,
  onChange,
}: {
  classId: string;
  groups: Group[];
  members: ClassMember[];
  onChange: () => void;
}) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [openInvite, setOpenInvite] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError('');

    try {
      await groupsApi.create(classId, newName);
      setNewName('');
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao criar o grupo.');
    }
  }

  async function handleAddMember(groupId: string, classMemberId: string) {
    if (!classMemberId) return;

    try {
      await groupsApi.addMember(groupId, classMemberId);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao adicionar.');
    }
  }

  async function handleRemoveMember(groupId: string, classMemberId: string) {
    try {
      await groupsApi.removeMember(groupId, classMemberId);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao remover.');
    }
  }

  async function handleSetLeader(groupId: string, classMemberId: string | null) {
    try {
      await groupsApi.update(groupId, { leaderId: classMemberId });
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao definir o líder.');
    }
  }

  async function handleDelete(group: Group) {
    const confirmed = window.confirm(`Excluir o grupo "${group.name}"?`);
    if (!confirmed) return;

    try {
      await groupsApi.remove(group.id);
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao excluir.');
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h3>Grupos ({groups.length})</h3>
      </div>

      <form onSubmit={handleCreate} className="inline-add">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nome do novo grupo"
          required
        />
        <button type="submit" className="primary">
          Criar grupo
        </button>
      </form>

      {error && <p className="form-error">{error}</p>}

      {groups.length === 0 && <p className="muted">Nenhum grupo criado ainda.</p>}

      {groups.map((group) => {
        const available = members.filter(
          (member) => !group.members.some((m) => m.classMemberId === member.id),
        );

        return (
          <article key={group.id} className="group-block">
            <div className="group-head">
              <h4>{group.name}</h4>
              <div className="button-row">
                <button
                  type="button"
                  onClick={() => setOpenInvite(openInvite === group.id ? null : group.id)}
                >
                  {openInvite === group.id ? 'Fechar QR' : 'QR do grupo'}
                </button>
                <a
                  className="button-link"
                  href={exportAiApi.url(classId, group.id)}
                  download
                  title="Planilha .xlsx com o projeto do grupo, para levar a uma IA"
                >
                  Exportar para IA
                </a>
                <button type="button" className="link-danger" onClick={() => void handleDelete(group)}>
                  Excluir
                </button>
              </div>
            </div>

            {openInvite === group.id && (
              <InvitePanel
                title={`QR Code — ${group.name}`}
                description="Só funciona para quem já está matriculado nesta turma."
                load={() => groupsApi.invite(group.id)}
                rotate={() => groupsApi.rotateInvite(group.id)}
                cancel={() => groupsApi.cancelInvite(group.id)}
              />
            )}

            {group.members.length === 0 ? (
              <p className="muted small">Sem integrantes.</p>
            ) : (
              <ul className="member-chips">
                {group.members.map((member) => (
                  <li key={member.classMemberId}>
                    <span>{member.name}</span>
                    {member.isLeader && <span className="badge badge-leader">Líder</span>}
                    {!member.isLeader && (
                      <button
                        type="button"
                        className="chip-action"
                        title="Definir como líder"
                        onClick={() => void handleSetLeader(group.id, member.classMemberId)}
                      >
                        líder
                      </button>
                    )}
                    <button
                      type="button"
                      className="chip-action chip-remove"
                      title="Remover do grupo"
                      onClick={() => void handleRemoveMember(group.id, member.classMemberId)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {available.length > 0 && (
              <select
                className="add-member-select"
                value=""
                onChange={(e) => void handleAddMember(group.id, e.target.value)}
              >
                <option value="">Adicionar aluno ao grupo…</option>
                {available.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            )}
          </article>
        );
      })}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Aluno
// ---------------------------------------------------------------------------

function StudentView({ classId }: { classId: string }) {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    groupsApi
      .list(classId)
      .then((result) => setGroups(result.groups))
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar os grupos.'),
      );
  }, [classId]);

  return (
    <>
      <ActivitiesSection classId={classId} />

    <section className="card">
      <h3>{groups && groups.length > 1 ? 'Seus grupos' : 'Seu grupo'}</h3>

      {error && <p className="form-error">{error}</p>}
      {!groups && !error && <p className="muted">Carregando…</p>}

      {groups?.length === 0 && (
        <p className="muted">
          Você ainda não está em nenhum grupo desta turma. O professor compartilha o QR
          Code de entrada do grupo diretamente com você.
        </p>
      )}

      {groups?.map((group) => (
        <article key={group.id} className="group-block">
          <h4>{group.name}</h4>
          {group.members.length === 0 ? (
            <p className="muted small">Sem integrantes.</p>
          ) : (
            <ul className="member-chips">
              {group.members.map((member) => (
                <li key={member.classMemberId}>
                  <span>{member.name}</span>
                  {member.isLeader && <span className="badge badge-leader">Líder</span>}
                </li>
              ))}
            </ul>
          )}
        </article>
      ))}
      </section>
    </>
  );
}
